import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { getStudioStats, type VendorStatsResponse } from '../../api/partnerBookings';
import { getMyKycStatus, type KycStatusResponse } from '../../api/partnerSettlement';
import { usePartnerContext } from './PartnerLayout';

function toDateParam(date: Date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function formatWon(value: number | null | undefined) {
  return `${(value ?? 0).toLocaleString('ko-KR')}원`;
}

export function PartnerDashboardPage() {
  const { studios, studiosLoading, studiosError } = usePartnerContext();

  const [studioId, setStudioId] = useState('');
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return toDateParam(d);
  });
  const [to, setTo] = useState(() => toDateParam(new Date()));
  const [groupBy, setGroupBy] = useState('day');

  const [stats, setStats] = useState<VendorStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');

  const [kyc, setKyc] = useState<KycStatusResponse | null>(null);
  const [kycChecked, setKycChecked] = useState(false);

  useEffect(() => {
    if (!studioId && studios.length > 0) {
      setStudioId(studios[0].studioId);
    }
  }, [studioId, studios]);

  useEffect(() => {
    let cancelled = false;
    getMyKycStatus()
      .then((data) => {
        if (!cancelled) setKyc(data);
      })
      .catch(() => {
        // 404 = 아직 정산 정책 없음 — 배너에서 제출 유도
      })
      .finally(() => {
        if (!cancelled) setKycChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadStats = useCallback(async () => {
    if (!studioId) return;
    setStatsLoading(true);
    setStatsError('');
    try {
      const data = await getStudioStats(studioId, { from, to, groupBy });
      setStats(data);
    } catch (error) {
      setStats(null);
      setStatsError(
        error instanceof ApiError && error.message
          ? error.message
          : '통계를 불러오지 못했습니다.',
      );
    } finally {
      setStatsLoading(false);
    }
  }, [from, groupBy, studioId, to]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  const maxBucketRevenue = useMemo(
    () => Math.max(1, ...(stats?.buckets ?? []).map((b) => b.revenueWon)),
    [stats],
  );

  if (studiosLoading) {
    return <p className="partner-empty">불러오는 중…</p>;
  }

  if (studiosError) {
    return <div className="partner-error">{studiosError}</div>;
  }

  if (studios.length === 0) {
    return (
      <div className="partner-card">
        <h2 className="partner-card__title">등록된 스튜디오가 없습니다</h2>
        <p className="partner-subtitle">첫 스튜디오를 등록하고 공간 운영을 시작해보세요.</p>
        <Link className="partner-btn" to="/partner/studios/new">
          스튜디오 등록
        </Link>
      </div>
    );
  }

  return (
    <>
      <h1 className="partner-title">대시보드</h1>
      <p className="partner-subtitle">스튜디오 운영 현황을 한눈에 확인하세요.</p>

      {kycChecked && (!kyc || kyc.kycStatus !== 'APPROVED') ? (
        <div className="partner-card">
          <h2 className="partner-card__title">
            정산 계좌 등록 필요
            <span className="partner-badge partner-badge--yellow">
              {kyc ? kyc.kycStatus : '미제출'}
            </span>
          </h2>
          <p className="partner-subtitle">
            정산을 받으려면 정산 계좌(KYC) 등록이 필요합니다.
          </p>
          <Link className="partner-btn partner-btn--ghost" to="/partner/settlement">
            정산 설정으로 이동
          </Link>
        </div>
      ) : null}

      <div className="partner-toolbar">
        <select
          aria-label="스튜디오 선택"
          className="partner-select"
          onChange={(event) => setStudioId(event.target.value)}
          value={studioId}
        >
          {studios.map((studio) => (
            <option key={studio.studioId} value={studio.studioId}>
              {studio.name}
            </option>
          ))}
        </select>
        <input
          aria-label="시작일"
          className="partner-input"
          onChange={(event) => setFrom(event.target.value)}
          type="date"
          value={from}
        />
        <span>~</span>
        <input
          aria-label="종료일"
          className="partner-input"
          onChange={(event) => setTo(event.target.value)}
          type="date"
          value={to}
        />
        <select
          aria-label="집계 단위"
          className="partner-select"
          onChange={(event) => setGroupBy(event.target.value)}
          value={groupBy}
        >
          <option value="day">일별</option>
          <option value="week">주별</option>
          <option value="month">월별</option>
        </select>
      </div>

      {statsError ? <div className="partner-error">{statsError}</div> : null}

      <div className="partner-card">
        <h2 className="partner-card__title">기간 요약</h2>
        {statsLoading ? (
          <p className="partner-empty">불러오는 중…</p>
        ) : stats ? (
          <div className="partner-stats-grid">
            <div className="partner-stat">
              <div className="partner-stat__label">총 매출</div>
              <div className="partner-stat__value">{formatWon(stats.totalRevenueWon)}</div>
            </div>
            <div className="partner-stat">
              <div className="partner-stat__label">총 예약</div>
              <div className="partner-stat__value">{stats.totalBookingCount}건</div>
            </div>
            <div className="partner-stat">
              <div className="partner-stat__label">승인</div>
              <div className="partner-stat__value">{stats.approvedCount}건</div>
            </div>
            <div className="partner-stat">
              <div className="partner-stat__label">대기</div>
              <div className="partner-stat__value">{stats.pendingCount}건</div>
            </div>
            <div className="partner-stat">
              <div className="partner-stat__label">거절</div>
              <div className="partner-stat__value">{stats.rejectedCount}건</div>
            </div>
            <div className="partner-stat">
              <div className="partner-stat__label">취소</div>
              <div className="partner-stat__value">{stats.cancelledCount}건</div>
            </div>
            <div className="partner-stat">
              <div className="partner-stat__label">고객 수</div>
              <div className="partner-stat__value">{stats.uniqueCustomerCount}명</div>
            </div>
            <div className="partner-stat">
              <div className="partner-stat__label">건당 평균 매출</div>
              <div className="partner-stat__value">{formatWon(stats.averageRevenuePerBooking)}</div>
            </div>
          </div>
        ) : (
          <p className="partner-empty">데이터가 없습니다.</p>
        )}
      </div>

      {stats && stats.buckets.length > 0 ? (
        <div className="partner-card">
          <h2 className="partner-card__title">추이</h2>
          <div className="partner-table-wrap">
            <table className="partner-table">
              <thead>
                <tr>
                  <th>구간</th>
                  <th style={{ width: '45%' }}>매출</th>
                  <th>예약</th>
                </tr>
              </thead>
              <tbody>
                {stats.buckets.map((bucket) => (
                  <tr key={bucket.key}>
                    <td>{bucket.key}</td>
                    <td>
                      <div
                        aria-hidden
                        style={{
                          background: '#24262c',
                          borderRadius: 4,
                          height: 10,
                          marginBottom: 4,
                          width: `${Math.max(2, Math.round((bucket.revenueWon / maxBucketRevenue) * 100))}%`,
                        }}
                      />
                      {formatWon(bucket.revenueWon)}
                    </td>
                    <td>{bucket.bookingCount}건</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}
